import React, { useEffect, useRef, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate } from "react-router-dom";
import "./listConfig.css";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import {
  addSidebarSection,
  updateSidebarSection,
  deleteSidebarSection,
  addSidebarSubSection,
  updateSidebarSubSection,
  deleteSidebarSubSection,
} from "../../Redux/Actions/settingsActions";
import AddImage from "../../Global Functions/addImage";
import FranchiseSidebarModal from "./franchiseSidebarModal";
import DeleteConfirmModal from "./deleteConfirmModal";

const FranchiseSidebarConfig = () => {
  const BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const iconInputRef = useRef(null);

  const { admin } = useSelector((state) => state.admin || {});
  const accessCode = admin?.data?.access_code || [];
  const canAdd = Array.isArray(accessCode) && accessCode.includes("add");
  const canEdit = Array.isArray(accessCode) && accessCode.includes("edit");

  const { loading: sidebarLoading } = useSelector((state) => state.sidebarSection || {});

  const [sections, setSections] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [sidebarModal, setSidebarModal] = useState(null);
  const [uploadingIcon, setUploadingIcon] = useState(false);
  const [iconDragOver, setIconDragOver] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchSections = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/setting/view`, {
        headers: { "Content-Type": "application/json" },
      });
      setSections(res.data?.data?.franchise_sidebar_sections || []);
    } catch (err) {
      toast.error("Failed to load sidebar sections");
    } finally {
      setLoadingList(false);
    }
  };

  useEffect(() => {
    fetchSections();
  }, []);

  const openAddSection = () =>
    setSidebarModal({ mode: "add-section", key: "", name: "", icon: "", order: sections.length + 1 });

  const openEditSection = (section) => setSidebarModal({ mode: "edit-section", ...section });

  const openAddSub = (sectionKey) =>
    setSidebarModal({ mode: "add-sub", section_key: sectionKey, key: "", name: "", icon: "", order: 0 });

  const openEditSub = (sectionKey, sub) =>
    setSidebarModal({ mode: "edit-sub", section_key: sectionKey, ...sub });

  const closeSidebarModal = () => {
    setSidebarModal(null);
    setUploadingIcon(false);
    setIconDragOver(false);
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const processIconFile = async (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    try {
      setUploadingIcon(true);
      const url = await AddImage(file);
      setSidebarModal((prev) => ({ ...prev, icon: url }));
    } catch (error) {
      toast.error("Icon upload failed");
    } finally {
      setUploadingIcon(false);
    }
  };

  const handleIconFileChange = (e) => {
    processIconFile(e.target.files?.[0]);
  };

  const handleIconDrop = (e) => {
    e.preventDefault();
    setIconDragOver(false);
    processIconFile(e.dataTransfer.files?.[0]);
  };

  const removeIcon = () => {
    setSidebarModal((prev) => ({ ...prev, icon: "" }));
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const submitSidebarModal = () => {
    const { mode, ...fields } = sidebarModal;
    const actionMap = {
      "add-section": addSidebarSection,
      "edit-section": updateSidebarSection,
      "add-sub": addSidebarSubSection,
      "edit-sub": updateSidebarSubSection,
    };
    const actionCreator = actionMap[mode];
    if (!actionCreator) return;

    dispatch(actionCreator(fields))
      .then(() => {
        setSidebarModal(null);
        fetchSections();
      })
      .catch((err) => toast.error(err.message || "Request failed"));
  };

  const deleteSection = (key, name) => setDeleteTarget({ type: "section", key, name });

  const deleteSub = (section_key, key, name) => setDeleteTarget({ type: "sub", section_key, key, name });

  const confirmDelete = () => {
    if (!deleteTarget) return;
    const { type, key, section_key } = deleteTarget;
    const action =
      type === "section" ? deleteSidebarSection({ key }) : deleteSidebarSubSection({ section_key, key });

    dispatch(action)
      .then(() => {
        setDeleteTarget(null);
        fetchSections();
      })
      .catch((err) => toast.error(err.message || "Delete failed"));
  };

  return (
    <MainLayout>
      <div className="fss-page">
        <div className="fss-breadcrumb">
          <button className="fss-back" onClick={() => navigate("/settings")} type="button" aria-label="Back">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <span>
            <Link to="/settings">Settings</Link> / <strong>Franchise Sidebar Menu</strong>
          </span>
        </div>

        <div className="fss-panel">
          <div className="fss-panel-head">
            <div>
              <h3>Franchise Sidebar Menu</h3>
              <p style={{ margin: "4px 0 0", fontSize: 13, color: "#6b7280" }}>
                Controls what shows in the franchise manager's sidebar navigation, and feeds
                every manager's default access control.
              </p>
            </div>
            {canAdd && (
              <button className="fss-add-btn" onClick={openAddSection}>
                + Add Section
              </button>
            )}
          </div>

          {loadingList ? (
            <div className="fss-empty">Loading…</div>
          ) : (
            <div className="fss-grid">
              {sections.length === 0 && (
                <div className="fss-empty">No sections added yet. Click "Add Section" to create the first one.</div>
              )}

              {[...sections]
                .sort((a, b) => (a.order || 0) - (b.order || 0))
                .map((section) => (
                  <div className="fss-card" key={section.key}>
                    <div className="fss-card-top">
                      <div className="fss-identity">
                        <div className="fss-icon">
                          {section.icon ? (
                            <img src={section.icon} alt={section.name} />
                          ) : (
                            <span className="fss-icon-fallback">{section.name?.slice(0, 2).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="fss-title">
                          <h4>{section.name}</h4>
                          <span>{section.key}</span>
                        </div>
                      </div>
                      <span className={`fss-status fss-status--${section.is_active ? "active" : "inactive"}`}>
                        <span className="fss-status-dot" />
                        {section.is_active ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div className="fss-order">
                      Position <strong>#{section.order}</strong>
                    </div>

                    {section.sub_sections?.length > 0 && (
                      <div className="fss-subs">
                        <span className="fss-subs-label">Sub-sections</span>
                        <div className="fss-chip-list">
                          {section.sub_sections.map((sub) => (
                            <span
                              className="fss-chip"
                              key={sub.key}
                              onClick={() => canEdit && openEditSub(section.key, sub)}
                            >
                              {sub.name}
                              {canEdit && (
                                <span
                                  className="fss-chip-remove"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    deleteSub(section.key, sub.key, sub.name);
                                  }}
                                >
                                  ×
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="fss-actions">
                      {canEdit && (
                        <button className="fss-action-btn" onClick={() => openEditSection(section)}>
                          Edit
                        </button>
                      )}
                      {canAdd && (
                        <button className="fss-action-btn fss-action-btn--primary" onClick={() => openAddSub(section.key)}>
                          + Sub
                        </button>
                      )}
                      {canEdit && (
                        <button className="fss-action-btn fss-action-btn--danger" onClick={() => deleteSection(section.key, section.name)}>
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>

      {/* {sidebarModal && (
        <div className="popup-overlay">
          <div className="popup-modal fss-modal">
            <h3>
              {sidebarModal.mode === "add-section" && "Add Section"}
              {sidebarModal.mode === "edit-section" && "Edit Section"}
              {sidebarModal.mode === "add-sub" && "Add Sub-section"}
              {sidebarModal.mode === "edit-sub" && "Edit Sub-section"}
            </h3>

            <div className="fss-field">
              <label>
                Key {sidebarModal.mode.startsWith("edit") && <span className="fss-field-hint">(locked)</span>}
              </label>
              <input
                type="text"
                className="fss-input"
                value={sidebarModal.key}
                disabled={sidebarModal.mode.startsWith("edit")}
                onChange={(e) =>
                  setSidebarModal((prev) => ({
                    ...prev,
                    key: e.target.value.trim().toLowerCase().replace(/\s+/g, "_"),
                  }))
                }
              />
            </div>

            <div className="fss-field">
              <label>Name</label>
              <input
                type="text"
                className="fss-input"
                value={sidebarModal.name}
                onChange={(e) => setSidebarModal((prev) => ({ ...prev, name: e.target.value }))}
              />
            </div>

            <div className="fss-field">
              <label>Order</label>
              <input
                type="number"
                className="fss-input"
                value={sidebarModal.order}
                onChange={(e) => setSidebarModal((prev) => ({ ...prev, order: Number(e.target.value) }))}
              />
            </div>

            <div className="fss-field">
              <label>Icon</label>

              {sidebarModal.icon ? (
                <div className="fss-icon-uploaded">
                  <div className="fss-icon-uploaded-img-wrap">
                    {uploadingIcon && (
                      <div className="fss-icon-uploading-overlay">
                        <svg className="fss-spinner" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                        </svg>
                      </div>
                    )}
                    <img src={sidebarModal.icon} alt="" />
                  </div>
                  <div className="fss-icon-uploaded-actions">
                    <button
                      type="button"
                      className="fss-upload-btn"
                      onClick={() => iconInputRef.current?.click()}
                      disabled={uploadingIcon}
                    >
                      Change
                    </button>
                    <button
                      type="button"
                      className="fss-upload-btn fss-upload-btn--danger"
                      onClick={removeIcon}
                      disabled={uploadingIcon}
                    >
                      Remove
                    </button>
                  </div>
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleIconFileChange}
                  />
                </div>
              ) : (
                <div
                  className={`fss-icon-dropzone ${iconDragOver ? "fss-icon-dropzone--over" : ""}`}
                  onClick={() => iconInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); setIconDragOver(true); }}
                  onDragLeave={() => setIconDragOver(false)}
                  onDrop={handleIconDrop}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && iconInputRef.current?.click()}
                >
                  {uploadingIcon ? (
                    <span>Uploading…</span>
                  ) : (
                    <>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <rect x="3" y="3" width="18" height="18" rx="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>{iconDragOver ? "Drop image here" : "Click or drag an icon here"}</span>
                    </>
                  )}
                  <input
                    ref={iconInputRef}
                    type="file"
                    accept="image/*"
                    style={{ display: "none" }}
                    onChange={handleIconFileChange}
                  />
                </div>
              )}
            </div>

            {sidebarModal.mode.startsWith("edit") && (
              <label className="fss-active-toggle">
                <input
                  type="checkbox"
                  checked={!!sidebarModal.is_active}
                  onChange={(e) => setSidebarModal((prev) => ({ ...prev, is_active: e.target.checked }))}
                />
                Active
              </label>
            )}

            <div className="fss-modal-actions">
              <button onClick={closeSidebarModal}>Cancel</button>
              <button onClick={submitSidebarModal} disabled={sidebarLoading}>
                {sidebarLoading ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )} */}

      <FranchiseSidebarModal
        sidebarModal={sidebarModal}
        setSidebarModal={setSidebarModal}
        sidebarLoading={sidebarLoading}
        uploadingIcon={uploadingIcon}
        iconDragOver={iconDragOver}
        setIconDragOver={setIconDragOver}
        iconInputRef={iconInputRef}
        handleIconFileChange={handleIconFileChange}
        handleIconDrop={handleIconDrop}
        removeIcon={removeIcon}
        closeSidebarModal={closeSidebarModal}
        submitSidebarModal={submitSidebarModal}
      />

      <DeleteConfirmModal
        deleteTarget={deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        loading={sidebarLoading}
      />

    </MainLayout>
  );
};

export default FranchiseSidebarConfig;